import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  DollarSign,
  ExternalLink,
  FileSearch,
  FileText,
  Globe,
  Info,
  Lightbulb,
  Loader2,
  MessageCircleWarning,
  MessageSquare,
  Minus,
  Play,
  Search,
  Stethoscope,
  ThumbsDown,
  ThumbsUp,
  TrendingDown,
  TrendingUp,
  User,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { InspectArtifactModal } from '@/components/artifact-inspector/InspectArtifactModal';
import { MoBrainIcon } from '@/components/mobrain/MoBrainIcon';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  type CitationSourceType,
  type ClientSentiment,
  type FeedbackThumb,
  type RecommendedAction,
  type RiskSnapshotSchema,
  type RiskTier,
  type SentimentSensitivity,
  type SentimentTrend,
  useGetLatestRiskSnapshotQuery,
  useSubmitRiskSnapshotFeedbackMutation,
  useTriggerRiskAssessmentMutation,
} from '@/services/api/mobrainService';

// --- Citation source config ---

const CITATION_SOURCE_CONFIG: Record<
  CitationSourceType,
  { label: string; icon: React.ElementType; className: string }
> = {
  encounter: {
    label: 'Encounter',
    icon: Stethoscope,
    className: 'border-blue-200 text-blue-700 bg-blue-50',
  },
  artifact: {
    label: 'Document',
    icon: FileText,
    className: 'border-violet-200 text-violet-700 bg-violet-50',
  },
  checkin: {
    label: 'Check-in',
    icon: CheckCircle2,
    className: 'border-green-200 text-green-700 bg-green-50',
  },
  checkin_notes: {
    label: 'Check-in Notes',
    icon: CheckCircle2,
    className: 'border-green-200 text-green-700 bg-green-50',
  },
  sms: {
    label: 'SMS',
    icon: MessageSquare,
    className: 'border-amber-200 text-amber-700 bg-amber-50',
  },
  expense: {
    label: 'Expense',
    icon: DollarSign,
    className: 'border-orange-200 text-orange-700 bg-orange-50',
  },
  provider: {
    label: 'Provider',
    icon: User,
    className: 'border-teal-200 text-teal-700 bg-teal-50',
  },
  prior_snapshot: {
    label: 'Prior Assessment',
    icon: Info,
    className: 'border-gray-200 text-gray-700 bg-gray-50',
  },
  web_research: {
    label: 'Web Research',
    icon: Globe,
    className: 'border-indigo-200 text-indigo-700 bg-indigo-50',
  },
  unknown: {
    label: 'Source',
    icon: FileSearch,
    className: 'border-gray-200 text-gray-600 bg-gray-50',
  },
};

// --- Helpers ---

const RISK_TIER_CONFIG: Record<
  RiskTier,
  { label: string; color: string; bg: string; accent: string }
> = {
  LOW: {
    label: 'Low Risk',
    color: 'text-green-700',
    bg: 'bg-green-50 border-green-200',
    accent: 'border-l-green-500',
  },
  MEDIUM: {
    label: 'Medium Risk',
    color: 'text-yellow-700',
    bg: 'bg-yellow-50 border-yellow-200',
    accent: 'border-l-yellow-500',
  },
  HIGH: {
    label: 'High Risk',
    color: 'text-orange-700',
    bg: 'bg-orange-50 border-orange-200',
    accent: 'border-l-orange-500',
  },
  CRITICAL: {
    label: 'Critical Risk',
    color: 'text-red-700',
    bg: 'bg-red-50 border-red-200',
    accent: 'border-l-red-500',
  },
  UNKNOWN: {
    label: 'Unknown',
    color: 'text-gray-600',
    bg: 'bg-gray-50 border-gray-200',
    accent: 'border-l-gray-400',
  },
};

const ACTION_LABELS: Record<
  RecommendedAction,
  { label: string; icon: React.ElementType }
> = {
  CONTACT_CLIENT: { label: 'Contact Client', icon: Search },
  CONFIRM_COVERAGE_LIMITS: { label: 'Confirm Coverage Limits', icon: Search },
  ESCALATE_CARE: { label: 'Escalate Care', icon: AlertTriangle },
  CONTINUE_CONSERVATIVE_CARE: {
    label: 'Continue Conservative Care',
    icon: CheckCircle2,
  },
  CONTINUE_AGGRESSIVE_CARE: {
    label: 'Continue Aggressive Care',
    icon: AlertTriangle,
  },
  MOVE_TO_DEMAND_REVIEW: { label: 'Move to Demand Review', icon: FileSearch },
  URGENT_ATTORNEY_ESCALATION: {
    label: 'Urgent Attorney Escalation',
    icon: AlertTriangle,
  },
};

const TIER_GRADE_COLORS: Record<string, string> = {
  A: 'text-green-700 bg-green-50 border-green-200',
  B: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  C: 'text-yellow-700 bg-yellow-50 border-yellow-200',
  D: 'text-orange-700 bg-orange-50 border-orange-200',
  E: 'text-red-600 bg-red-50 border-red-200',
  F: 'text-red-700 bg-red-50 border-red-200',
};

function formatCurrency(value: string | null): string {
  if (!value) return '--';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatPercent(value: number | null): string {
  if (value === null || value === undefined) return '--';
  return `${(value * 100).toFixed(0)}%`;
}

function formatDateTimeLines(
  value: string | null,
): { dateLine: string; timeLine: string } {
  if (!value) return { dateLine: 'Unknown', timeLine: '' };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { dateLine: 'Unknown', timeLine: '' };

  const dateLine = parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeLine = parsed.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return { dateLine, timeLine };
}

// --- Summary Parsing ---

function parseSummaryShort(summary: string): {
  overview: string;
  nextSteps: string[];
} {
  const nextStepsIdx = summary.indexOf('Next steps:');
  if (nextStepsIdx === -1) {
    return { overview: summary.trim(), nextSteps: [] };
  }
  const overview = summary.slice(0, nextStepsIdx).trim();
  const stepsSection = summary.slice(nextStepsIdx + 'Next steps:'.length);
  const nextSteps = stepsSection
    .split(/\n/)
    .map((line) => line.replace(/^\s*[\u2022-]\s*/, '').trim())
    .filter(Boolean);
  return { overview, nextSteps };
}

const FormattedSummary: React.FC<{
  summary: string;
  clampOverview?: boolean;
}> = ({ summary, clampOverview }) => {
  const { overview, nextSteps } = parseSummaryShort(summary);
  return (
    <div className="space-y-2">
      <p
        className={cn(
          'text-sm text-gray-600 leading-relaxed',
          clampOverview && 'line-clamp-2',
        )}
      >
        {overview}
      </p>
      {nextSteps.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1">Next steps:</p>
          <ul className="space-y-0.5">
            {nextSteps.map((step, i) => (
              <li
                key={i}
                className="flex items-start gap-1.5 text-sm text-gray-600 leading-relaxed"
              >
                <span className="text-gray-400 mt-0.5 flex-shrink-0">•</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// --- Sub-components ---

/**
 * Multi-color score gauge with 4 tier segments and animated needle.
 */
const ScoreGauge: React.FC<{ score: number; tier: RiskTier }> = ({
  score,
  tier,
}) => {
  const tierConfig = RISK_TIER_CONFIG[tier];
  const rotation = (score / 100) * 180 - 90;

  // Arc helper: generate SVG arc path from startAngle to endAngle (degrees, 0=left, 180=right)
  const arcPath = (startDeg: number, endDeg: number): string => {
    const cx = 100;
    const cy = 100;
    const r = 85;
    const startRad = ((180 + startDeg) * Math.PI) / 180;
    const endRad = ((180 + endDeg) * Math.PI) / 180;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  // v2 tier thresholds: LOW 0-29, MEDIUM 30-54, HIGH 55-74, CRITICAL 75-100
  const segments = [
    { start: 0, end: 54, color: '#22c55e' },   // green  0-29
    { start: 54, end: 99, color: '#eab308' },   // yellow 30-54
    { start: 99, end: 135, color: '#f97316' },  // orange 55-74
    { start: 135, end: 180, color: '#ef4444' },  // red    75-100
  ];

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-48 h-24 overflow-hidden">
        <svg viewBox="0 0 200 105" className="w-full h-full">
          {/* Background track */}
          <path
            d={arcPath(0, 180)}
            fill="none"
            stroke="#f3f4f6"
            strokeWidth="14"
            strokeLinecap="round"
          />
          {/* Colored segments */}
          {segments.map((seg) => (
            <path
              key={seg.start}
              d={arcPath(seg.start, seg.end)}
              fill="none"
              stroke={seg.color}
              strokeWidth="14"
              strokeLinecap="butt"
              opacity={0.85}
            />
          ))}
          {/* Needle */}
          <line
            x1="100"
            y1="100"
            x2="100"
            y2="28"
            stroke="#1f2937"
            strokeWidth="2.5"
            strokeLinecap="round"
            transform={`rotate(${rotation} 100 100)`}
            className="transition-transform duration-700 ease-out"
          />
          <circle cx="100" cy="100" r="5" fill="#1f2937" />
        </svg>
      </div>
      <div className="text-center -mt-1">
        <span className={cn('text-3xl font-bold', tierConfig.color)}>
          {score}
        </span>
        <span className="text-sm text-gray-500 font-medium ml-1">/ 100</span>
      </div>
      <Badge className={cn('border', tierConfig.bg, tierConfig.color)}>
        {tierConfig.label}
      </Badge>
    </div>
  );
};

const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  count?: number;
}> = ({ title, icon: Icon, children, count }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">{title}</span>
          {count !== undefined && count > 0 && (
            <Badge variant="secondary" className="text-xs">
              {count}
            </Badge>
          )}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Cup Pressure Card ---

function parseNum(value: string | null): number {
  if (!value) return 0;
  const n = parseFloat(value);
  return Number.isNaN(n) ? 0 : n;
}

const CupPressureCard: React.FC<{ snapshot: RiskSnapshotSchema }> = ({
  snapshot,
}) => {
  const confirmedLimit = parseNum(snapshot.confirmed_insurance_limit);
  const estimatedLimit = parseNum(snapshot.estimated_insurance_limit);
  const effectiveLimit = parseNum(snapshot.limit_for_scoring);
  const confirmedCost = parseNum(snapshot.confirmed_cost_so_far);
  const estimatedCost = parseNum(snapshot.estimated_cost_so_far);
  const projectedFuture = parseNum(snapshot.estimated_projected_future_cost);
  const overLimit = parseNum(snapshot.projected_over_limit_amount);
  const clientNet = parseNum(snapshot.client_net_estimate);

  const currentTotal = confirmedCost + estimatedCost;
  const totalProjected = currentTotal + projectedFuture;

  // Scale: bar always shows the full picture — whichever is bigger wins,
  // with 10% padding so markers don't sit at the very edge.
  const rawMax = Math.max(
    effectiveLimit,
    confirmedLimit,
    estimatedLimit,
    totalProjected,
    1,
  );
  const barMax = rawMax * 1.1;
  const pct = (v: number) => Math.max(0, Math.min((v / barMax) * 100, 100));

  const confirmedCostPct = pct(confirmedCost);
  const estimatedCostPct = pct(estimatedCost);
  const futurePct = pct(projectedFuture);
  // If neither specific limit is available, fall back to effectiveLimit
  const hasAnySpecificLimit = confirmedLimit > 0 || estimatedLimit > 0;
  const confirmedLimitPct = confirmedLimit > 0 ? pct(confirmedLimit) : null;
  const estimatedLimitPct =
    estimatedLimit > 0 && estimatedLimit !== confirmedLimit
      ? pct(estimatedLimit)
      : null;
  const effectiveLimitPct =
    !hasAnySpecificLimit && effectiveLimit > 0 ? pct(effectiveLimit) : null;
  const isOverLimit = overLimit > 0;

  // Where does the over-limit zone start?
  const overLimitStartPct = effectiveLimit > 0 ? pct(effectiveLimit) : null;
  const overLimitEndPct =
    isOverLimit && overLimitStartPct !== null
      ? pct(totalProjected)
      : null;

  const tierColor =
    TIER_GRADE_COLORS[snapshot.coverage_tier ?? ''] ??
    'text-gray-600 bg-gray-50 border-gray-200';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-gray-500" />
            Cost vs. Coverage
          </CardTitle>
          {snapshot.coverage_tier && (
            <Badge
              variant="outline"
              className={cn('text-xs font-semibold', tierColor)}
            >
              Tier {snapshot.coverage_tier}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* ── Bar visualization ── */}
        <div className="relative pt-9 pb-1">
          {/* Limit value labels — positioned above the bar */}
          {confirmedLimitPct !== null && (
            <div
              className="absolute top-0 -translate-x-1/2 text-center"
              style={{ left: `${confirmedLimitPct}%` }}
            >
              <div className="text-[10px] font-medium text-gray-500">Confirmed</div>
              <div className="text-[11px] font-semibold text-gray-700">
                {formatCurrency(String(confirmedLimit))}
              </div>
            </div>
          )}
          {estimatedLimitPct !== null && (
            <div
              className="absolute top-0 -translate-x-1/2 text-center"
              style={{ left: `${estimatedLimitPct}%` }}
            >
              <div className="text-[10px] font-medium text-gray-500">Estimated</div>
              <div className="text-[11px] font-semibold text-gray-500">
                {formatCurrency(String(estimatedLimit))}
              </div>
            </div>
          )}
          {effectiveLimitPct !== null && (
            <div
              className="absolute top-0 -translate-x-1/2 text-center"
              style={{ left: `${effectiveLimitPct}%` }}
            >
              <div className="text-[10px] font-medium text-gray-500">
                {snapshot.confirmed_insurance_limit ? 'Confirmed' : 'Estimated'}
              </div>
              <div className="text-[11px] font-semibold text-gray-600">
                {formatCurrency(String(effectiveLimit))}
              </div>
            </div>
          )}

          {/* The bar */}
          <div className="relative h-8 bg-gray-100 rounded-lg overflow-visible">
            {/* Confirmed / actual cost */}
            {confirmedCost > 0 && (
              <div
                className="absolute inset-y-0 left-0 rounded-l-lg bg-emerald-500 transition-all duration-700"
                style={{ width: `${confirmedCostPct}%` }}
              />
            )}

            {/* Estimated cost to date — striped to distinguish from confirmed */}
            {estimatedCost > 0 && (
              <div
                className="absolute inset-y-0 transition-all duration-700"
                style={{
                  left: `${confirmedCostPct}%`,
                  width: `${estimatedCostPct}%`,
                  background:
                    'repeating-linear-gradient(135deg, #2dd4bf 0px, #2dd4bf 4px, #5eead4 4px, #5eead4 8px)',
                }}
              />
            )}

            {/* Future projected — lighter, dashed feel */}
            {projectedFuture > 0 && (
              <div
                className="absolute inset-y-0 transition-all duration-700"
                style={{
                  left: `${confirmedCostPct + estimatedCostPct}%`,
                  width: `${futurePct}%`,
                  background:
                    'repeating-linear-gradient(135deg, #fbbf24 0px, #fbbf24 4px, #fde68a 4px, #fde68a 8px)',
                }}
              />
            )}

            {/* Over-limit red overlay */}
            {isOverLimit &&
              overLimitStartPct !== null &&
              overLimitEndPct !== null &&
              overLimitEndPct > overLimitStartPct && (
                <div
                  className="absolute inset-y-0 bg-red-500/30 border-l-2 border-red-400"
                  style={{
                    left: `${overLimitStartPct}%`,
                    width: `${overLimitEndPct - overLimitStartPct}%`,
                  }}
                />
              )}

            {/* Confirmed limit marker */}
            {confirmedLimitPct !== null && (
              <div
                className="absolute -top-1 -bottom-1 w-0.5 bg-gray-800 z-10"
                style={{ left: `${confirmedLimitPct}%` }}
              />
            )}

            {/* Estimated limit marker (dashed) */}
            {estimatedLimitPct !== null && (
              <div
                className="absolute -top-1 -bottom-1 z-10"
                style={{
                  left: `${estimatedLimitPct}%`,
                  borderLeft: '2px dashed #6b7280',
                }}
              />
            )}

            {/* Effective limit fallback marker */}
            {effectiveLimitPct !== null && (
              <div
                className="absolute -top-1 -bottom-1 w-0.5 bg-gray-700 z-10"
                style={{ left: `${effectiveLimitPct}%` }}
              />
            )}
          </div>
        </div>

        {/* ── Legend ── */}
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-emerald-500" />
            <span className="font-medium">
              Actual {formatCurrency(String(confirmedCost))}
            </span>
          </div>
          {estimatedCost > 0 && (
            <div className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm"
                style={{
                  background:
                    'repeating-linear-gradient(135deg, #2dd4bf 0px, #2dd4bf 2px, #5eead4 2px, #5eead4 4px)',
                }}
              />
              <span className="font-medium">
                Estimated {formatCurrency(String(estimatedCost))}
              </span>
            </div>
          )}
          {projectedFuture > 0 && (
            <div className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded-sm"
                style={{
                  background:
                    'repeating-linear-gradient(135deg, #fbbf24 0px, #fbbf24 2px, #fde68a 2px, #fde68a 4px)',
                }}
              />
              <span className="font-medium">
                Future {formatCurrency(String(projectedFuture))}
              </span>
            </div>
          )}
          {confirmedLimitPct !== null && (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-gray-800 rounded" />
              <span className="font-medium">Confirmed Limit</span>
            </div>
          )}
          {estimatedLimitPct !== null && (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0 border-t-2 border-dashed border-gray-500" />
              <span className="font-medium">Estimated Limit</span>
            </div>
          )}
        </div>

        {/* ── Summary row ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-gray-100">
          <div>
            <span className="text-[11px] text-gray-500 font-medium block uppercase tracking-wide">
              Spent to Date
            </span>
            <span className="text-sm font-bold text-gray-900">
              {formatCurrency(String(currentTotal))}
            </span>
          </div>
          <div>
            <span className="text-[11px] text-gray-500 font-medium block uppercase tracking-wide">
              Total Projected
            </span>
            <span
              className={cn(
                'text-sm font-bold',
                isOverLimit ? 'text-red-600' : 'text-gray-900',
              )}
            >
              {formatCurrency(String(totalProjected))}
            </span>
          </div>
          <div>
            <span className="text-[11px] text-gray-500 font-medium block uppercase tracking-wide">
              {confirmedLimit > 0
                ? 'Confirmed Limit'
                : estimatedLimit > 0
                  ? 'Estimated Limit'
                  : effectiveLimit > 0
                    ? 'Estimated Limit'
                    : 'Limit'}
            </span>
            <span className="text-sm font-bold text-gray-900">
              {formatCurrency(
                String(confirmedLimit || estimatedLimit || effectiveLimit),
              )}
            </span>
          </div>
          {clientNet > 0 && (
            <div>
              <span className="text-[11px] text-gray-500 font-medium block uppercase tracking-wide">
                Client Net
              </span>
              <span
                className={cn(
                  'text-sm font-bold',
                  snapshot.net_projection_below_25
                    ? 'text-red-600'
                    : 'text-gray-900',
                )}
              >
                {formatCurrency(snapshot.client_net_estimate)}
                {snapshot.client_net_ratio !== null && (
                  <span className="text-xs font-medium text-gray-500 ml-1">
                    ({formatPercent(snapshot.client_net_ratio)})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// --- Sentiment Badge ---

const SENTIMENT_CONFIG: Record<
  ClientSentiment,
  { label: string; borderClass: string; textClass: string; bgClass: string }
> = {
  POSITIVE: {
    label: 'Positive',
    borderClass: 'border-green-300',
    textClass: 'text-green-700',
    bgClass: 'bg-green-50',
  },
  NEUTRAL: {
    label: 'Neutral',
    borderClass: 'border-gray-300',
    textClass: 'text-gray-700',
    bgClass: 'bg-gray-50',
  },
  NEGATIVE: {
    label: 'Negative',
    borderClass: 'border-red-300',
    textClass: 'text-red-700',
    bgClass: 'bg-red-50',
  },
};

const TREND_CONFIG: Record<
  SentimentTrend,
  { icon: React.ElementType; label: string; className: string }
> = {
  IMPROVING: { icon: TrendingUp, label: 'Improving', className: 'text-green-600' },
  STABLE: { icon: Minus, label: 'Stable', className: 'text-gray-500' },
  DECLINING: { icon: TrendingDown, label: 'Declining', className: 'text-red-600' },
};

const SENSITIVITY_LABEL: Record<SentimentSensitivity, string> = {
  LOW_SENSITIVITY: 'Low Sensitivity',
  MODERATE_SENSITIVITY: 'Moderate Sensitivity',
  HIGH_SENSITIVITY: 'High Sensitivity',
};

const SentimentBadge: React.FC<{
  sentiment: ClientSentiment;
  trend: SentimentTrend | null;
  sensitivity: SentimentSensitivity | null;
  negativeCount: number;
  reasoning: string | null;
}> = ({ sentiment, trend, sensitivity, negativeCount, reasoning }) => {
  const config = SENTIMENT_CONFIG[sentiment];
  const trendConfig = trend ? TREND_CONFIG[trend] : null;
  const TrendIcon = trendConfig?.icon ?? null;

  const tooltipParts: string[] = [];
  if (reasoning) tooltipParts.push(reasoning);
  if (sensitivity) tooltipParts.push(SENSITIVITY_LABEL[sensitivity]);
  if (negativeCount > 0)
    tooltipParts.push(`${negativeCount} prior negative${negativeCount > 1 ? 's' : ''}`);

  const badge = (
    <Badge
      variant="outline"
      className={cn(
        'text-xs w-full justify-center gap-1',
        config.borderClass,
        config.textClass,
        config.bgClass,
        tooltipParts.length > 0 && 'cursor-help',
      )}
    >
      {sentiment === 'NEGATIVE' && (
        <MessageCircleWarning className="h-3 w-3 flex-shrink-0" />
      )}
      Sentiment: {config.label}
      {TrendIcon && (
        <TrendIcon className={cn('h-3 w-3', trendConfig?.className)} />
      )}
    </Badge>
  );

  if (tooltipParts.length === 0) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs font-normal">
        {reasoning && (
          <p className="text-xs leading-relaxed">
            &ldquo;{reasoning}&rdquo;
          </p>
        )}
        {(sensitivity || negativeCount > 0) && (
          <p className={cn('text-xs text-gray-300', reasoning && 'mt-1')}>
            {[sensitivity ? SENSITIVITY_LABEL[sensitivity] : null, negativeCount > 0 ? `${negativeCount} prior negative${negativeCount > 1 ? 's' : ''}` : null].filter(Boolean).join(' · ')}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

// --- Main Component ---

interface RiskSnapshotPanelProps {
  matterId: string;
}

export const RiskSnapshotPanel: React.FC<RiskSnapshotPanelProps> = ({
  matterId,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [feedbackThumb, setFeedbackThumb] = useState<FeedbackThumb | null>(
    null,
  );
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [citationModalOpen, setCitationModalOpen] = useState(false);
  const [inspectArtifactId, setInspectArtifactId] = useState<string | null>(null);

  const {
    data: snapshot,
    isLoading,
    error,
  } = useGetLatestRiskSnapshotQuery(matterId, {
    skip: !matterId,
    pollingInterval: isRunning ? 5000 : undefined,
  });

  const [triggerAssessment, { isLoading: isTriggering }] =
    useTriggerRiskAssessmentMutation();

  const [submitFeedback, { isLoading: isSubmittingFeedback }] =
    useSubmitRiskSnapshotFeedbackMutation();

  const [triggeredAtSnapshotId, setTriggeredAtSnapshotId] = useState<
    string | null
  >(null);

  // Sync feedback state from snapshot
  useEffect(() => {
    if (snapshot) {
      setFeedbackThumb(snapshot.feedback_thumb);
      setFeedbackText(snapshot.feedback_text ?? '');
      setFeedbackSaved(false);
    }
  }, [snapshot?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      isRunning &&
      snapshot?.id &&
      (triggeredAtSnapshotId === null || snapshot.id !== triggeredAtSnapshotId)
    ) {
      setIsRunning(false);
      setTriggeredAtSnapshotId(null);
    }
  }, [isRunning, snapshot?.id, triggeredAtSnapshotId]);

  // Safety timeout: stop polling after 2 minutes if no new snapshot arrives
  useEffect(() => {
    if (!isRunning) return undefined;
    const timeout = setTimeout(() => {
      setIsRunning(false);
      setTriggeredAtSnapshotId(null);
    }, 120_000);
    return () => clearTimeout(timeout);
  }, [isRunning]);

  const handleTrigger = useCallback(async () => {
    if (!matterId || isTriggering || isRunning) return;
    setTriggeredAtSnapshotId(snapshot?.id ?? null);
    setIsRunning(true);
    try {
      await triggerAssessment(matterId).unwrap();
    } catch {
      setIsRunning(false);
      setTriggeredAtSnapshotId(null);
    }
  }, [matterId, isTriggering, isRunning, snapshot?.id, triggerAssessment]);

  const handleFeedbackSelect = useCallback(
    (thumb: FeedbackThumb) => {
      setFeedbackThumb(thumb);
      setFeedbackSaved(false);
      if (thumb === 'UP') {
        submitFeedback({
          matterId,
          feedback: { feedback_thumb: thumb },
        })
          .unwrap()
          .then(() => {
            setFeedbackSaved(true);
            setTimeout(() => setFeedbackSaved(false), 2000);
          })
          .catch(() => {
            setFeedbackThumb(null);
          });
      } else {
        setFeedbackModalOpen(true);
      }
    },
    [matterId, submitFeedback],
  );

  const handleSaveFeedback = useCallback(async () => {
    if (!matterId || !feedbackThumb || isSubmittingFeedback) return;
    try {
      await submitFeedback({
        matterId,
        feedback: {
          feedback_thumb: feedbackThumb,
          feedback_text: feedbackText || undefined,
        },
      }).unwrap();
      setFeedbackModalOpen(false);
      setFeedbackSaved(true);
      setTimeout(() => setFeedbackSaved(false), 2000);
    } catch {
      // Feedback submission failed — keep modal open so user can retry
    }
  }, [
    matterId,
    feedbackThumb,
    feedbackText,
    isSubmittingFeedback,
    submitFeedback,
  ]);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-32 bg-gray-200 rounded" />
          <div className="h-20 bg-gray-200 rounded" />
          <div className="h-20 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    const is404 =
      typeof error === 'object' &&
      'status' in error &&
      error.status === 404;

    if (is404) {
      return (
        <div className="p-6">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <MoBrainIcon className="h-12 w-12 opacity-30 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">
              No Risk Snapshot Yet
            </h3>
            <p className="text-sm text-gray-500 max-w-md mb-4">
              MoBrain will automatically generate a risk assessment when
              treatment, coverage, or ledger data is updated for this matter.
            </p>
            <button
              type="button"
              onClick={handleTrigger}
              disabled={isTriggering || isRunning}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {isRunning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run MoBrain Now
                </>
              )}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-medium">
            Failed to load risk snapshot
          </span>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <MoBrainIcon className="h-12 w-12 opacity-30 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">
            No Risk Snapshot Yet
          </h3>
          <p className="text-sm text-gray-500 max-w-md">
            MoBrain will automatically generate a risk assessment when
            treatment, coverage, or ledger data is updated for this matter.
          </p>
        </div>
      </div>
    );
  }

  const actionConfig = snapshot.recommended_action
    ? ACTION_LABELS[snapshot.recommended_action]
    : null;
  const ActionIcon = actionConfig?.icon ?? Info;
  const snapshotGeneratedAt =
    snapshot.created_at ??
    snapshot.run_finished_at ??
    snapshot.run_started_at ??
    snapshot.input_snapshot_at;
  const snapshotGeneratedAtLines = formatDateTimeLines(snapshotGeneratedAt);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="p-6 space-y-5"
    >
      {/* Running Banner */}
      <AnimatePresence>
        {isRunning && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
              <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
              <span className="text-sm font-medium text-blue-700">
                MoBrain is running a new risk assessment...
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Header */}
      <div className="border border-gray-200 rounded-lg p-5">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left: Gauge */}
          <div className="flex-shrink-0 flex flex-col items-center">
            {snapshot.risk_score !== null && snapshot.risk_tier ? (
              <ScoreGauge
                score={snapshot.risk_score}
                tier={snapshot.risk_tier}
              />
            ) : (
              <div className="text-gray-500 text-sm py-8">No score</div>
            )}
          </div>

          {/* Center: Action + Summary (gets most space) */}
          <div className="flex-1 flex flex-col gap-3 min-w-0">
            {actionConfig ? (
              <div className="flex items-center gap-3">
                <ActionIcon
                  className={cn(
                    'h-6 w-6 flex-shrink-0',
                    snapshot.recommended_action ===
                      'URGENT_ATTORNEY_ESCALATION'
                      ? 'text-red-500'
                      : snapshot.recommended_action === 'MOVE_TO_DEMAND_REVIEW'
                        ? 'text-orange-500'
                        : 'text-primary',
                  )}
                />
                <span className="text-lg font-semibold text-gray-900">
                  {actionConfig.label}
                </span>
              </div>
            ) : (
              <span className="text-sm text-gray-500">
                No recommendation available
              </span>
            )}
            {snapshot.summary_short && (
              <FormattedSummary summary={snapshot.summary_short} />
            )}
          </div>

          {/* Right: Re-run top, pills centered, feedback bottom */}
          <div className="flex-shrink-0 w-44 flex flex-col items-end">
            {/* Re-run at top */}
            <button
              type="button"
              onClick={handleTrigger}
              disabled={isTriggering || isRunning}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors mb-2"
            >
              {isRunning ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-3 w-3" />
                  Re-run
                </>
              )}
            </button>

            {/* Pills stacked vertically, centered */}
            <div className="flex-1 flex flex-col gap-1.5 justify-center w-full">
              {snapshot.confidence !== null && (
                <Badge
                  variant="outline"
                  className="border-gray-300 text-gray-600 text-xs w-full justify-center"
                >
                  {formatPercent(snapshot.confidence)} confidence
                </Badge>
              )}
              {snapshot.stale_or_incomplete && (
                <Badge
                  variant="outline"
                  className="border-amber-300 text-amber-700 bg-amber-50 text-xs w-full justify-center"
                >
                  Incomplete
                </Badge>
              )}
              {snapshot.estimated_flag &&
                snapshot.estimated_flag !== 'NONE' && (
                  <Badge
                    variant="outline"
                    className="border-blue-300 text-blue-700 bg-blue-50 text-xs w-full justify-center"
                  >
                    {snapshot.estimated_flag === 'HEAVY'
                      ? 'Mostly Estimated'
                      : 'Partially Estimated'}
                  </Badge>
                )}
              {snapshot.policy_search_recommended && (
                <Badge
                  variant="outline"
                  className="border-purple-300 text-purple-700 bg-purple-50 text-xs w-full justify-center"
                >
                  <Search className="h-3 w-3 mr-1" />
                  Policy Search
                  {snapshot.policy_search_urgency
                    ? ` · ${snapshot.policy_search_urgency}`
                    : ''}
                </Badge>
              )}
              {snapshot.client_sentiment && (
                <SentimentBadge
                  sentiment={snapshot.client_sentiment}
                  trend={snapshot.client_sentiment_trend}
                  sensitivity={snapshot.client_sensitivity}
                  negativeCount={snapshot.negative_sentiment_count}
                  reasoning={snapshot.client_sentiment_reasoning}
                />
              )}
            </div>

            {/* Snapshot time + feedback at bottom right */}
            <div className="mt-2 self-start w-full flex items-center gap-2 flex-nowrap whitespace-nowrap">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleFeedbackSelect('UP')}
                  disabled={isSubmittingFeedback}
                  className={cn(
                    'p-2 rounded transition-colors',
                    feedbackThumb === 'UP'
                      ? 'text-green-600 bg-green-50'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50',
                  )}
                  title="Agree"
                >
                  <ThumbsUp className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFeedbackSelect('DOWN')}
                  disabled={isSubmittingFeedback}
                  className={cn(
                    'p-2 rounded transition-colors',
                    feedbackThumb === 'DOWN'
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50',
                  )}
                  title="Disagree"
                >
                  <ThumbsDown className="h-5 w-5" />
                </button>
                <AnimatePresence>
                  {feedbackSaved && (
                    <motion.span
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-xs text-green-600 font-medium ml-1"
                    >
                      Saved
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <span className="text-[10px] text-gray-500 leading-tight flex flex-col">
                <span>{snapshotGeneratedAtLines.dateLine}</span>
                {snapshotGeneratedAtLines.timeLine && (
                  <span>{snapshotGeneratedAtLines.timeLine}</span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Overview — Cup Pressure Visual */}
      <CupPressureCard snapshot={snapshot} />

      {/* v2: Care Path Signals */}
      {(snapshot.fork_state || snapshot.milestone_status || snapshot.pain_progression_state || snapshot.pd_severity || (snapshot.action_urgency && snapshot.action_urgency !== 'STANDARD')) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Info className="h-4 w-4 text-gray-500" />
              Care Path Signals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {snapshot.fork_state && (
                <div>
                  <span className="text-xs text-gray-500">Fork State</span>
                  <div className="text-sm font-medium text-gray-900">
                    {snapshot.fork_state.replace('_', ' ')}
                  </div>
                </div>
              )}
              {snapshot.milestone_status && (
                <div>
                  <span className="text-xs text-gray-500">Milestone</span>
                  <div className="text-sm font-medium">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        snapshot.milestone_status === 'ON_TRACK' && 'border-green-200 text-green-700 bg-green-50',
                        snapshot.milestone_status === 'AT_RISK' && 'border-yellow-200 text-yellow-700 bg-yellow-50',
                        snapshot.milestone_status === 'OVERDUE' && 'border-orange-200 text-orange-700 bg-orange-50',
                        snapshot.milestone_status === 'BLOCKED' && 'border-red-200 text-red-700 bg-red-50',
                      )}
                    >
                      {snapshot.milestone_status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              )}
              {snapshot.pain_progression_state && (
                <div>
                  <span className="text-xs text-gray-500">Pain Progression</span>
                  <div className="text-sm font-medium">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        snapshot.pain_progression_state === 'IMPROVING' && 'border-green-200 text-green-700 bg-green-50',
                        snapshot.pain_progression_state === 'STABLE' && 'border-gray-200 text-gray-700 bg-gray-50',
                        snapshot.pain_progression_state === 'WORSENING' && 'border-red-200 text-red-700 bg-red-50',
                        snapshot.pain_progression_state === 'STALE' && 'border-amber-200 text-amber-700 bg-amber-50',
                      )}
                    >
                      {snapshot.pain_progression_state}
                    </Badge>
                  </div>
                </div>
              )}
              {snapshot.pd_severity && (
                <div>
                  <span className="text-xs text-gray-500">PD Severity</span>
                  <div className="text-sm font-medium">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        snapshot.pd_severity === 'MINOR' && 'border-green-200 text-green-700 bg-green-50',
                        snapshot.pd_severity === 'MODERATE' && 'border-yellow-200 text-yellow-700 bg-yellow-50',
                        snapshot.pd_severity === 'SEVERE' && 'border-red-200 text-red-700 bg-red-50',
                        snapshot.pd_severity === 'UNKNOWN' && 'border-gray-200 text-gray-700 bg-gray-50',
                      )}
                    >
                      {snapshot.pd_severity}
                    </Badge>
                  </div>
                </div>
              )}
              {snapshot.action_urgency && snapshot.action_urgency !== 'STANDARD' && (
                <div>
                  <span className="text-xs text-gray-500">Action Urgency</span>
                  <div className="text-sm font-medium">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        snapshot.action_urgency === 'IMMEDIATE' && 'border-red-200 text-red-700 bg-red-50',
                        snapshot.action_urgency === 'DEFERRED' && 'border-gray-200 text-gray-600 bg-gray-50',
                      )}
                    >
                      {snapshot.action_urgency}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
            {/* Secondary actions */}
            {snapshot.secondary_actions && snapshot.secondary_actions.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-500">Secondary Actions</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {snapshot.secondary_actions.map((action, i) => (
                    <Badge key={i} variant="outline" className="text-xs border-gray-200 text-gray-600">
                      {action.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Investigation Details — all collapsed */}
      <div className="space-y-2">
        {snapshot.facts_used.length > 0 && (
          <CollapsibleSection
            title="Facts Used"
            icon={CheckCircle2}
            count={snapshot.facts_used.length}
          >
            <ul className="space-y-1.5">
              {snapshot.facts_used.map((fact, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0">
                    {i + 1}.
                  </span>
                  {fact}
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {snapshot.assumptions.length > 0 && (
          <CollapsibleSection
            title="Assumptions"
            icon={Lightbulb}
            count={snapshot.assumptions.length}
          >
            <ul className="space-y-1.5">
              {snapshot.assumptions.map((assumption, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0">
                    {i + 1}.
                  </span>
                  {assumption}
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {snapshot.missing_inputs.length > 0 && (
          <CollapsibleSection
            title="Missing Inputs"
            icon={AlertTriangle}
            count={snapshot.missing_inputs.length}
          >
            <ul className="space-y-1.5">
              {snapshot.missing_inputs.map((input, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2">
                  <span className="text-gray-500 font-medium flex-shrink-0">
                    {i + 1}.
                  </span>
                  {input}
                </li>
              ))}
            </ul>
          </CollapsibleSection>
        )}

        {snapshot.confidence_reasoning && (
          <CollapsibleSection title="Confidence Reasoning" icon={Info}>
            <p className="text-sm text-gray-700 leading-relaxed">
              {snapshot.confidence_reasoning}
            </p>
          </CollapsibleSection>
        )}

        {(() => {
          const enriched = snapshot.citations_enriched ?? [];
          const citationCount = enriched.length || snapshot.citations.length;
          if (citationCount === 0) return null;

          // Use enriched citations if available, otherwise fall back to raw
          const hasEnriched = enriched.length > 0;

          return (
            <CollapsibleSection
              title="Citations"
              icon={FileSearch}
              count={citationCount}
            >
              <div className="space-y-1.5">
                {hasEnriched
                  ? enriched.slice(0, 5).map((cit, i) => {
                      const config =
                        CITATION_SOURCE_CONFIG[cit.source_type] ??
                        CITATION_SOURCE_CONFIG.unknown;
                      const Icon = config.icon;
                      const isClickable = cit.source_type === 'artifact' && !!cit.artifact_id;

                      return (
                        <button
                          key={i}
                          type="button"
                          disabled={!isClickable}
                          onClick={() => {
                            if (isClickable && cit.artifact_id) {
                              setInspectArtifactId(cit.artifact_id);
                            }
                          }}
                          className={cn(
                            'flex items-center gap-2 text-xs rounded-md px-2.5 py-1.5 w-full text-left transition-colors',
                            isClickable
                              ? 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                              : 'bg-gray-50 cursor-default',
                          )}
                        >
                          <Badge
                            variant="outline"
                            className={cn('text-[10px] shrink-0 flex items-center gap-1', config.className)}
                          >
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </Badge>
                          <span className="truncate text-gray-700">
                            {cit.display_name}
                          </span>
                          {isClickable && (
                            <ExternalLink className="h-3 w-3 ml-auto shrink-0 text-gray-400" />
                          )}
                        </button>
                      );
                    })
                  : snapshot.citations.slice(0, 5).map((citation, i) => {
                      const parts = citation.split(':');
                      const source = parts.length > 1 ? parts[0] : 'source';
                      const detail = parts.length > 1 ? parts.slice(1).join(':') : citation;
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs text-gray-700 bg-gray-50 rounded-md px-2.5 py-1.5"
                        >
                          <Badge variant="outline" className="text-[10px] shrink-0 uppercase">
                            {source}
                          </Badge>
                          <span className="truncate">{detail.trim()}</span>
                        </div>
                      );
                    })}
                {citationCount > 5 && (
                  <p className="text-xs text-gray-500">
                    +{citationCount - 5} more
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setCitationModalOpen(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors mt-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  View all citations
                </button>
              </div>
            </CollapsibleSection>
          );
        })()}

        {snapshot.scoring_breakdown &&
          snapshot.scoring_breakdown.red_flags.length > 0 && (
            <CollapsibleSection
              title="Red Flags"
              icon={AlertTriangle}
              count={snapshot.scoring_breakdown.red_flags.length}
            >
              <div className="flex flex-wrap gap-1.5">
                {snapshot.scoring_breakdown.red_flags.map((flag, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="border-red-200 text-red-700 bg-red-50 text-xs"
                  >
                    {flag}
                  </Badge>
                ))}
              </div>
            </CollapsibleSection>
          )}
      </div>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackModalOpen}
        onOpenChange={(open) => {
          setFeedbackModalOpen(open);
          if (!open) {
            setFeedbackThumb(snapshot?.feedback_thumb ?? null);
            setFeedbackText(snapshot?.feedback_text ?? '');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Disagree with Assessment</DialogTitle>
            <DialogDescription>
              Your feedback will be considered in the next risk assessment run.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="What do you disagree with? How should the assessment change?"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            rows={4}
          />
          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setFeedbackModalOpen(false);
                setFeedbackThumb(snapshot?.feedback_thumb ?? null);
                setFeedbackText(snapshot?.feedback_text ?? '');
              }}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveFeedback}
              disabled={isSubmittingFeedback}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {isSubmittingFeedback && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Save Feedback
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Citation Viewer Dialog */}
      <Dialog
        open={
          citationModalOpen &&
          !!snapshot &&
          ((snapshot.citations_enriched ?? []).length > 0 || snapshot.citations.length > 0)
        }
        onOpenChange={setCitationModalOpen}
      >
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-4 w-4" />
              Citations (
              {(snapshot?.citations_enriched ?? []).length ||
                (snapshot?.citations.length ?? 0)}
              )
            </DialogTitle>
            <DialogDescription>
              Sources referenced during the risk assessment. Click documents to
              inspect.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-2">
            {(snapshot?.citations_enriched ?? []).length > 0
              ? (snapshot?.citations_enriched ?? []).map((cit, i) => {
                  const config =
                    CITATION_SOURCE_CONFIG[cit.source_type] ??
                    CITATION_SOURCE_CONFIG.unknown;
                  const Icon = config.icon;
                  const isClickable =
                    cit.source_type === 'artifact' && !!cit.artifact_id;

                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={!isClickable}
                      onClick={() => {
                        if (isClickable && cit.artifact_id) {
                          setInspectArtifactId(cit.artifact_id);
                        }
                      }}
                      className={cn(
                        'flex items-start gap-3 rounded-lg border px-3 py-2.5 w-full text-left transition-colors',
                        isClickable
                          ? 'border-gray-200 bg-white hover:bg-gray-50 cursor-pointer'
                          : 'border-gray-100 bg-gray-50 cursor-default',
                      )}
                    >
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] shrink-0 flex items-center gap-1 mt-0.5',
                          config.className,
                        )}
                      >
                        <Icon className="h-3 w-3" />
                        {config.label}
                      </Badge>
                      <span className="text-sm text-gray-700 leading-relaxed break-words flex-1">
                        {cit.display_name}
                      </span>
                      {isClickable && (
                        <ExternalLink className="h-3.5 w-3.5 mt-0.5 shrink-0 text-gray-400" />
                      )}
                    </button>
                  );
                })
              : snapshot?.citations.map((citation, i) => {
                  const parts = citation.split(':');
                  const source = parts.length > 1 ? parts[0] : 'source';
                  const detail =
                    parts.length > 1 ? parts.slice(1).join(':') : citation;
                  return (
                    <div
                      key={i}
                      className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
                    >
                      <Badge
                        variant="outline"
                        className="text-[10px] shrink-0 uppercase mt-0.5"
                      >
                        {source}
                      </Badge>
                      <span className="text-sm text-gray-700 leading-relaxed break-words">
                        {detail.trim()}
                      </span>
                    </div>
                  );
                })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Inspect Artifact Modal (opened when clicking document citations) */}
      {inspectArtifactId && (
        <InspectArtifactModal
          artifactId={inspectArtifactId}
          isOpen={!!inspectArtifactId}
          onOpenChange={(open) => {
            if (!open) setInspectArtifactId(null);
          }}
          matterId={matterId}
        />
      )}
    </motion.div>
  );
};
